<template>
  <div class="flex flex-col gap-4">
    <ProcedureArchivedWarningAlert v-if="procedure?.status === 'archived'" :procedure="procedure" />

    <Card>
      <CardHeader class="flex flex-row items-start justify-between gap-4">
        <div class="min-w-0 space-y-3">
          <div class="flex flex-wrap items-center gap-2">
            <CardTitle class="truncate">
              {{ procedureTitle }}
            </CardTitle>
            <Badge variant="outline">
              v{{ procedureVersionLabel }}
            </Badge>
            <Badge :variant="procedureStatusBadgeVariant">
              {{ procedureStatusLabel }}
            </Badge>
            <Badge v-if="activeDraftId !== null && procedure?.status !== 'draft'" variant="secondary">
              Draft in progress
            </Badge>
          </div>

          <p v-if="procedureGoal" class="text-sm text-muted-foreground">
            Goal: {{ procedureGoal }}
          </p>
          <p v-else class="text-sm text-muted-foreground">
            No goal defined yet.
          </p>
        </div>

        <Button v-if="canEditProcedure" size="sm" :disabled="isEditDisabled" @click="startEditingProcedure">
          {{ editButtonLabel }}
        </Button>
      </CardHeader>

      <CardContent class="flex flex-col gap-4">
        <p v-if="procedureStore.isLoading" class="text-sm text-muted-foreground">
          Loading procedure...
        </p>
        <p v-else-if="!procedure" class="text-sm text-muted-foreground">
          Procedure not found.
        </p>
        <template v-else>
          <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div class="rounded-md border border-input/70 p-3">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">
                Status
              </p>
              <div class="mt-2">
                <Badge :variant="procedureStatusBadgeVariant">
                  {{ procedureStatusLabel }}
                </Badge>
              </div>
            </div>

            <div class="rounded-md border border-input/70 p-3">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">
                Version
              </p>
              <p class="mt-2 text-sm font-medium">
                v{{ procedureVersionLabel }}
              </p>
            </div>

            <div class="rounded-md border border-input/70 p-3">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">
                Last updated
              </p>
              <p class="mt-2 text-sm font-medium">
                {{ updatedAtLabel }}
              </p>
            </div>

            <div class="rounded-md border border-input/70 p-3">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">
                Published
              </p>
              <p class="mt-2 text-sm font-medium">
                {{ publishedAtLabel }}
              </p>
            </div>
          </div>

          <Separator />

          <div v-if="hasProcedureContent" class="tiptap-viewer rounded-md border border-input bg-transparent">
            <EditorContent :editor="viewerEditor" />
          </div>
          <div v-else class="rounded-md border border-dashed border-input/70 px-4 py-8 text-sm text-muted-foreground">
            No procedure steps have been documented yet.
          </div>

          <ProcedureImportedDocuments :procedure="procedure" />
        </template>
      </CardContent>
    </Card>

    <ProcedureShowHistory :business-system-id="businessSystemId" :procedure-id="procedureId" />
  </div>
</template>

<script setup>
import Image from '@tiptap/extension-image'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Badge } from '../../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../../resources/js/components/ui/card/index.js'
import { Separator } from '../../../../../../../../resources/js/components/ui/separator/index.js'
import { useApiAction } from '../../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import ProcedureArchivedWarningAlert from '../../../components/business-systems/ProcedureArchivedWarningAlert.vue'
import ProcedureImportedDocuments from '../../../components/business-systems/ProcedureImportedDocuments.vue'
import ProcedureShowHistory from '../../../components/business-systems/ProcedureShowHistory.vue'
import { useProcedureStore } from '../../../stores/procedures.js'

const route = useRoute()
const router = useRouter()
const procedureStore = useProcedureStore()

const createEmptyEditorDocument = () => {
  return {
    type: 'doc',
    content: [{
      type: 'paragraph',
    }],
  }
}
const sanitizeTiptapMarks = (marks) => {
  if (!Array.isArray(marks)) {
    return undefined
  }

  const sanitizedMarks = marks
    .map((mark) => {
      if (!mark || typeof mark !== 'object' || Array.isArray(mark)) {
        return null
      }

      const type = typeof mark.type === 'string' ? mark.type : ''

      if (type === '') {
        return null
      }

      const sanitizedMark = {
        type,
      }

      if (mark.attrs && typeof mark.attrs === 'object' && !Array.isArray(mark.attrs)) {
        sanitizedMark.attrs = { ...mark.attrs }
      }

      return sanitizedMark
    })
    .filter((mark) => mark !== null)

  if (sanitizedMarks.length === 0) {
    return undefined
  }

  return sanitizedMarks
}
const sanitizeTiptapNode = (node) => {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return null
  }

  const nodeType = typeof node.type === 'string' ? node.type : ''

  if (nodeType === '') {
    return null
  }

  if (nodeType === 'text') {
    if (node.text === null || node.text === undefined) {
      return null
    }

    const sanitizedTextNode = {
      type: 'text',
      text: typeof node.text === 'string' ? node.text : String(node.text),
    }
    const sanitizedMarks = sanitizeTiptapMarks(node.marks)

    if (sanitizedMarks) {
      sanitizedTextNode.marks = sanitizedMarks
    }

    return sanitizedTextNode
  }

  const sanitizedNode = {
    type: nodeType,
  }

  if (node.attrs && typeof node.attrs === 'object' && !Array.isArray(node.attrs)) {
    sanitizedNode.attrs = { ...node.attrs }
  }

  if (Array.isArray(node.content)) {
    sanitizedNode.content = node.content
      .map((childNode) => sanitizeTiptapNode(childNode))
      .filter((childNode) => childNode !== null)
  }

  return sanitizedNode
}
const sanitizeProcedureDocument = (content) => {
  const normalizedContent = Array.isArray(content?.content) ? content.content : []
  const sanitizedContent = normalizedContent
    .map((node) => sanitizeTiptapNode(node))
    .filter((node) => node !== null)

  if (sanitizedContent.length === 0) {
    sanitizedContent.push({
      type: 'paragraph',
    })
  }

  return {
    type: 'doc',
    content: sanitizedContent,
  }
}
const createDocumentFromText = (textContent) => {
  if (typeof textContent !== 'string' || textContent.trim() === '') {
    return createEmptyEditorDocument()
  }

  return {
    type: 'doc',
    content: [{
      type: 'paragraph',
      content: [{
        type: 'text',
        text: textContent.trim(),
      }],
    }],
  }
}
const normalizeProcedureContent = (content, fallbackTextContent = '') => {
  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    return createDocumentFromText(fallbackTextContent)
  }

  if (content.type !== 'doc') {
    return createDocumentFromText(fallbackTextContent)
  }

  return sanitizeProcedureDocument(content)
}
const documentHasRenderableContent = (documentNode) => {
  if (!documentNode || typeof documentNode !== 'object' || Array.isArray(documentNode)) {
    return false
  }

  if (documentNode.type === 'text') {
    return typeof documentNode.text === 'string' && documentNode.text.trim() !== ''
  }

  if (documentNode.type === 'image') {
    return typeof documentNode.attrs?.src === 'string' && documentNode.attrs.src.trim() !== ''
  }

  if (!Array.isArray(documentNode.content)) {
    return false
  }

  return documentNode.content.some((childNode) => documentHasRenderableContent(childNode))
}

const viewerEditor = useEditor({
  extensions: [StarterKit, Image],
  content: createEmptyEditorDocument(),
  editable: false,
  editorProps: {
    attributes: {
      class: 'min-h-[16rem] w-full px-4 py-4 text-sm focus:outline-none',
    },
  },
})

const businessSystemId = computed(() => Number(route.params.businessSystem ?? 0))
const procedureId = computed(() => Number(route.params.procedure ?? 0))
const procedure = computed(() => procedureStore.getItemById(procedureId.value))
const activeDraftId = computed(() => {
  const activeDraftRawId = Number(procedure.value?.active_draft?.id ?? 0)

  if (!Number.isFinite(activeDraftRawId) || activeDraftRawId <= 0) {
    return null
  }

  return activeDraftRawId
})
const procedureTitle = computed(() => {
  if (!procedure.value?.title) {
    return 'Procedure'
  }

  return procedure.value.title
})
const procedureGoal = computed(() => {
  if (typeof procedure.value?.cached?.goal === 'string' && procedure.value.cached.goal.trim() !== '') {
    return procedure.value.cached.goal.trim()
  }

  return ''
})
const procedureDocument = computed(() => {
  return normalizeProcedureContent(procedure.value?.content, procedure.value?.text_content ?? '')
})
const hasProcedureContent = computed(() => {
  return documentHasRenderableContent(procedureDocument.value)
})
const procedureVersionLabel = computed(() => formatVersion(procedure.value?.version))
const procedureStatusLabel = computed(() => formatStatus(procedure.value?.status))
const procedureStatusBadgeVariant = computed(() => {
  if (procedure.value?.status === 'published') {
    return 'default'
  }

  if (procedure.value?.status === 'draft') {
    return 'secondary'
  }

  return 'outline'
})
const updatedAtLabel = computed(() => formatTimestamp(procedure.value?.updated_at, 'Not updated yet'))
const publishedAtLabel = computed(() => formatTimestamp(procedure.value?.published_at, 'Not published'))
const canEditProcedure = computed(() => {
  return procedure.value?.status === 'draft'
    || procedure.value?.status === 'published'
    || activeDraftId.value !== null
})
const editButtonLabel = computed(() => {
  if (startingEdit.value) {
    return 'Preparing Draft...'
  }

  if (procedure.value?.status === 'draft' || activeDraftId.value !== null) {
    return 'Continue Edit'
  }

  return 'Edit'
})
const isEditDisabled = computed(() => {
  return startingEdit.value || procedureStore.isLoading || !procedure.value
})

const formatVersion = (versionValue) => {
  const normalizedVersion = Number(versionValue)

  if (!Number.isFinite(normalizedVersion)) {
    return '1.00'
  }

  return normalizedVersion.toFixed(2)
}

const formatStatus = (statusValue) => {
  const normalizedStatus = String(statusValue ?? '').trim()

  if (normalizedStatus === '') {
    return 'Unknown'
  }

  return normalizedStatus
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const formatTimestamp = (timestampValue, fallbackLabel) => {
  const normalizedTimestamp = typeof timestampValue === 'string' ? timestampValue : ''
  const parsedTimestamp = Date.parse(normalizedTimestamp)

  if (Number.isNaN(parsedTimestamp)) {
    return fallbackLabel
  }

  return new Date(parsedTimestamp).toLocaleString()
}

const syncViewerWithProcedure = (documentContent) => {
  if (!viewerEditor.value) {
    return
  }

  viewerEditor.value.commands.setContent(documentContent, false)
}

const fetchProcedure = async () => {
  if (!Number.isFinite(procedureId.value) || procedureId.value <= 0) {
    return
  }

  await procedureStore.fetchItemById(procedureId.value)
}

const { execute: executeStartEdit, loading: startingEdit } = useApiAction((id) => {
  return procedureStore.startEditProcedure(id)
}, {
  onSuccess: async (draftProcedure) => {
    await router.push({
      name: 'ProcedureEditPage',
      params: {
        businessSystem: businessSystemId.value,
        procedure: draftProcedure.id,
      },
    })
  },
})

const startEditingProcedure = async () => {
  if (!canEditProcedure.value) {
    return
  }

  if (procedure.value?.status === 'draft') {
    await router.push({
      name: 'ProcedureEditPage',
      params: {
        businessSystem: businessSystemId.value,
        procedure: procedureId.value,
      },
    })

    return
  }

  if (activeDraftId.value !== null) {
    await router.push({
      name: 'ProcedureEditPage',
      params: {
        businessSystem: businessSystemId.value,
        procedure: activeDraftId.value,
      },
    })

    return
  }

  await executeStartEdit(procedureId.value)
}

onMounted(() => {
  fetchProcedure()
})

onBeforeUnmount(() => {
  viewerEditor.value?.destroy()
})

watch(viewerEditor, (currentEditor) => {
  if (!currentEditor) {
    return
  }

  syncViewerWithProcedure(procedureDocument.value)
}, {
  immediate: true,
})

watch(procedureDocument, (currentProcedureDocument) => {
  syncViewerWithProcedure(currentProcedureDocument)
}, {
  immediate: true,
})

watch(procedureId, () => {
  fetchProcedure()
})
</script>

<style scoped>
.tiptap-viewer :deep(.ProseMirror) {
  white-space: pre-wrap;
}

.tiptap-viewer :deep(.ProseMirror ul),
.tiptap-viewer :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
}

.tiptap-viewer :deep(.ProseMirror ul) {
  list-style-type: disc;
}

.tiptap-viewer :deep(.ProseMirror ol) {
  list-style-type: decimal;
}

.tiptap-viewer :deep(.ProseMirror h2) {
  font-size: 1.25rem;
  font-weight: 600;
}

.tiptap-viewer :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}
</style>
